import { useState } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { paymentService } from "../../services/paymentService";

interface PayTabsRefundDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  orderId: string;
  amount: number;
  currency: string;
}

/**
 * Single refund dialog for PayTabs. Replaces the previous Tabby/Tamara pair.
 *
 * Refunds are routed server-side through POST /api/paytabs/refund which calls
 * the gateway first and refuses to mutate local state if the refund is
 * rejected — a "refunded" record with no money moved is worse than a visible
 * failure.
 */
export function PayTabsRefundDialog({
  isOpen,
  onClose,
  onSuccess,
  orderId,
  amount,
  currency,
}: PayTabsRefundDialogProps) {
  const [refundAmount, setRefundAmount] = useState(amount);
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!refundAmount || refundAmount <= 0) {
      setError("Please enter a valid refund amount");
      return;
    }
    if (refundAmount > amount) {
      setError("Refund amount cannot exceed payment amount");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      await paymentService.refundPayTabsPayment({
        orderId,
        amount: refundAmount,
        reason: reason || undefined,
      });
      onSuccess();
      onClose();
      setRefundAmount(amount);
      setReason("");
    } catch (err: any) {
      setError(err.message || "Failed to process refund");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Refund PayTabs Payment"
      className="max-w-md"
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          Refund will be issued through PayTabs. The order status updates only
          after PayTabs confirms the refund.
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Refund Amount ({currency})
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            max={amount}
            value={refundAmount}
            onChange={(e) => setRefundAmount(parseFloat(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-gray-500">
            Maximum: {amount.toFixed(2)} {currency}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason (optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoading}
            placeholder="Why is this payment being refunded?"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Refund"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
